import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, useAnimation, PanInfo, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Kitchen } from "../../types/index";

// --- EXTENDED TYPES FOR HIERARCHY ---
interface DialItem {
  id: string;
  label: string;
  image: string;
  type: 'kitchen' | 'category' | 'dish' | 'addon';
  price?: string;
  children?: DialItem[]; 
}

interface RotaryDialProps {
  kitchens?: Kitchen[];
  onSelect?: (item: DialItem) => void;
}

// --- ASSETS ---
const ASSETS = {
  DIAL: "/dial1.png",
  RING: "/brassring.png",
  STOP: "/fingerstop.png",
  BTN: "/selectorbutton.png",
  BG: "/kitchenbg.png",
  NAMEPLATE: "/nameplate.png",
};

// --- CONFIGURATION ---
const CONFIG = {
  DIAL_SIZE: 300,
  RADIUS: 85,
  HOLE_SIZE: 60,
  ANGLE_STEP: 60,
  VISIBLE_SLOTS: 5,
  RING_SIZE: 700,
  STOP: { X: 190, Y: 35, ROTATE: 12 },
  BTN_SIZE: 100,
  INITIAL_ANGLE: 30,
  TOP_OFFSET: 14,
  DRAG_SENSITIVITY: 0.6,
};

// --- MOCK DATA GENERATOR ---
const enrichKitchenData = (kitchens: Kitchen[]): DialItem[] => {
  return kitchens.map((k, index) => ({
    id: k.id || `kitchen-${index}`,
    label: k.name || "Unknown Kitchen",
    image: k.logo || k.image || "/placeholder_logo.png",
    type: 'kitchen',
    children: [
      { 
        id: 'c1', 
        label: 'Starters', 
        image: 'https://images.unsplash.com/photo-1541529086526-db283c563270?auto=format&fit=crop&w=200&q=80', 
        type: 'category', 
        children: [
          { id: 'd1', label: 'Spring Rolls', price: '₹200', image: 'https://images.unsplash.com/photo-1544025162-d76690b6d014?auto=format&fit=crop&w=200&q=80', type: 'dish' },
          { id: 'd2', label: 'Samosa', price: '₹150', image: 'https://images.unsplash.com/photo-1496116218417-7a1812d692eb?auto=format&fit=crop&w=200&q=80', type: 'dish' },
        ]
      },
      { 
        id: 'c2', 
        label: 'Mains', 
        image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=200&q=80', 
        type: 'category', 
        children: [
          { id: 'd3', label: 'Biryani', price: '₹350', image: 'https://images.unsplash.com/photo-1525755662778-989d0524087e?auto=format&fit=crop&w=200&q=80', type: 'dish', children: [
             { id: 'a1', label: 'Extra Spicy', image: '/chili.png', type: 'addon' },
             { id: 'a2', label: 'Extra Rice', image: '/rice.png', type: 'addon' }
          ]},
          { id: 'd4', label: 'Butter Chicken', price: '₹400', image: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?auto=format&fit=crop&w=200&q=80', type: 'dish' },
        ]
      },
      { 
        id: 'c3', 
        label: 'Breads', 
        image: 'https://images.unsplash.com/photo-1585521537151-1d5c45462023?auto=format&fit=crop&w=200&q=80', 
        type: 'category', 
        children: [
          { id: 'd5', label: 'Naan', price: '₹80', image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=200&q=80', type: 'dish' },
          { id: 'd6', label: 'Roti', price: '₹40', image: 'https://images.unsplash.com/photo-1534080564247-8f1e9e4c1173?auto=format&fit=crop&w=200&q=80', type: 'dish' },
        ]
      },
      { 
        id: 'c4', 
        label: 'Desserts', 
        image: 'https://images.unsplash.com/photo-1563729768647-d07c38779c1f?auto=format&fit=crop&w=200&q=80', 
        type: 'category', 
        children: [
          { id: 'd7', label: 'Gulab Jamun', price: '₹120', image: 'https://images.unsplash.com/photo-1585080876619-cd14e84a8e78?auto=format&fit=crop&w=200&q=80', type: 'dish' },
        ]
      },
      { 
        id: 'c5', 
        label: 'Drinks', 
        image: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=200&q=80', 
        type: 'category', 
        children: [
          { id: 'd8', label: 'Mango Lassi', price: '₹100', image: 'https://images.unsplash.com/photo-1599599810694-b5ac4dd64b91?auto=format&fit=crop&w=200&q=80', type: 'dish' },
        ]
      },
    ]
  }));
};

// --- TYPESCRIPT FIX FOR ANIMATEPRESENCE ---
const AnimatePresenceSafe = AnimatePresence as any;

const RotaryDial: React.FC<RotaryDialProps> = ({ kitchens: propKitchens = [], onSelect: onSelectProp }) => {
  const navigate = useNavigate();
  const controls = useAnimation();
  
  // --- STATE ---
  const [rotation, setRotation] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0); 
  const [isDragging, setIsDragging] = useState(false);

  // Navigation Stack
  const [navStack, setNavStack] = useState<DialItem[]>([]);
  const [history, setHistory] = useState<DialItem[]>([]); 

  // Derived Data
  const rootItems = useMemo(() => enrichKitchenData(propKitchens), [propKitchens]);
  
  const currentLevelItems = useMemo(() => {
    if (navStack.length === 0) return rootItems;
    const parent = navStack[navStack.length - 1];
    return parent.children || [];
  }, [navStack, rootItems]);

  const activeItem = useMemo(() => {
     if (!currentLevelItems.length) return null;
     return currentLevelItems[currentIndex % currentLevelItems.length];
  }, [currentLevelItems, currentIndex]);

  // --- LOGIC: INDEXING ---
  const normalizeIndex = useCallback((index: number, length: number) => {
    if (length === 0) return 0;
    return ((index % length) + length) % length;
  }, []);

  const getItemForVisualSlot = useCallback((slotIndex: number) => {
    if (!currentLevelItems.length) return null;
    const kIndex = normalizeIndex(currentIndex + slotIndex, currentLevelItems.length);
    return currentLevelItems[kIndex];
  }, [currentIndex, currentLevelItems, normalizeIndex]);

  // --- ACTIONS ---
  const snapToNearestSlot = useCallback(() => {
    const stepsRotated = Math.round(rotation / CONFIG.ANGLE_STEP);
    const newIndex = normalizeIndex(currentIndex - stepsRotated, currentLevelItems.length);

    setCurrentIndex(newIndex);
    setRotation(0); 
    
    controls.start({ 
      rotate: 0, 
      transition: { type: "spring", stiffness: 400, damping: 30 } 
    });
  }, [rotation, currentIndex, currentLevelItems.length, normalizeIndex, controls]);

  const handleSelectActive = useCallback(() => {
    if (!activeItem) return;

    if (activeItem.children && activeItem.children.length > 0) {
        setHistory(prev => [...prev, activeItem]);
        setNavStack(prev => [...prev, activeItem]);
        setCurrentIndex(0); 
        setRotation(0);
    } else {
        console.log("Selected Leaf:", activeItem);
        if (onSelectProp) onSelectProp(activeItem);
    }
  }, [activeItem, onSelectProp]);

  const handleBack = useCallback(() => {
    if (navStack.length === 0) return;
    
    const newStack = [...navStack];
    newStack.pop();
    setNavStack(newStack);
    
    const newHistory = [...history];
    newHistory.pop();
    setHistory(newHistory);
    
    setCurrentIndex(0);
    setRotation(0);
  }, [navStack, history]);

  const handleItemClick = useCallback((visualSlotIndex: number) => {
    if (isDragging) return;

    if (visualSlotIndex === 0) {
        handleSelectActive();
        return;
    }

    const targetRotation = -(visualSlotIndex * CONFIG.ANGLE_STEP);
    
    controls.start({
        rotate: targetRotation,
        transition: { duration: 0.5, ease: "circOut" }
    }).then(() => {
        const newIndex = normalizeIndex(currentIndex + visualSlotIndex, currentLevelItems.length);
        setCurrentIndex(newIndex);
        setRotation(0);
        controls.set({ rotate: 0 }); 
    });

  }, [isDragging, currentIndex, currentLevelItems.length, normalizeIndex, controls, handleSelectActive]);

  // --- INTERACTION HANDLERS ---
  const handlePan = (event: any, info: PanInfo) => {
    setRotation((prev) => prev + info.delta.y * CONFIG.DRAG_SENSITIVITY);
  };

  const handlePanEnd = () => {
    setIsDragging(false);
    snapToNearestSlot();
  };

  const centerImage = navStack.length > 0 ? navStack[navStack.length - 1].image : ASSETS.BTN;
  const centerLabel = navStack.length > 0 ? navStack[navStack.length - 1].label : (activeItem?.label || "SELECT");

  return (
    <div className="relative w-full h-[700px] flex flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-slate-900 to-zinc-900 pt-10 select-none font-sans">
      
      {/* BREADCRUMBS & BACK */}
      <div className="absolute top-4 left-0 right-0 z-50 px-4 flex justify-between items-start">
         <div className="flex flex-wrap gap-2">
            {history.map((h, i) => (
                <div key={h.id} className="flex items-center text-xs font-bold uppercase tracking-widest text-orange-500">
                    <span className="opacity-60">{h.label}</span>
                    {i < history.length - 1 && <span className="mx-2 text-white/20">/</span>}
                </div>
            ))}
         </div>
         {navStack.length > 0 && (
             <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBack}
                className="bg-gradient-to-r from-red-600/30 to-red-600/20 hover:from-red-600 hover:to-red-700 text-red-200 hover:text-white border border-red-500/50 hover:border-red-400 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all shadow-lg hover:shadow-red-500/30"
             >
                ← Back
             </motion.button>
         )}
      </div>

      {/* BACKGROUND (Contextual) */}
      <AnimatePresenceSafe mode="popLayout">
        <motion.div
          key={activeItem?.id || "bg"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.25 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0 bg-cover bg-center z-0 filter blur-2xl scale-125"
          style={{ backgroundImage: `url(${activeItem?.image || ASSETS.BG})` }}
        />
      </AnimatePresenceSafe>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 z-0" />

      {/* MAIN DIAL ASSEMBLY */}
      <div 
        className="relative z-10 flex items-center justify-center mb-8"
        style={{ width: CONFIG.RING_SIZE, height: CONFIG.RING_SIZE }}
      >
        {/* Static Brass Ring */}
        <div 
          className="absolute inset-0 z-20 pointer-events-none bg-contain bg-center bg-no-repeat opacity-90 drop-shadow-2xl"
          style={{ backgroundImage: `url(${ASSETS.RING})` }}
        />

        {/* Static Finger Stop */}
        <div 
          className="absolute z-40 pointer-events-none bg-contain bg-no-repeat drop-shadow-[0_15px_15px_rgba(0,0,0,0.9)]"
          style={{ 
            backgroundImage: `url(${ASSETS.STOP})`,
            width: '180px',
            height: '100px',
            bottom: `${CONFIG.STOP.Y}px`,
            right: `${CONFIG.STOP.X}px`,
            transform: `rotate(${CONFIG.STOP.ROTATE}deg)`
          }}
        />

        {/* Dynamic Rotating Disc */}
        <motion.div
          className="absolute z-30 rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          style={{ 
            width: CONFIG.DIAL_SIZE, 
            height: CONFIG.DIAL_SIZE,
            rotate: rotation,
            backgroundImage: `url(${ASSETS.DIAL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            top: '50%',
            left: '50%',
            marginTop: -(CONFIG.DIAL_SIZE / 2) + CONFIG.TOP_OFFSET,
            marginLeft: -(CONFIG.DIAL_SIZE / 2),
            touchAction: 'none'
          }}
          animate={controls}
          onPan={(e, i) => { setIsDragging(true); handlePan(e, i); }}
          onPanEnd={handlePanEnd}
        >
          {/* DIAL HOLES */}
          <AnimatePresenceSafe>
          {Array.from({ length: 10 }).map((_, i) => {
            const angleDeg = i * CONFIG.ANGLE_STEP + CONFIG.INITIAL_ANGLE; 
            const angleRad = (angleDeg * Math.PI) / 180;
            const x = CONFIG.RADIUS * Math.cos(angleRad);
            const y = CONFIG.RADIUS * Math.sin(angleRad);

            const item = getItemForVisualSlot(i);
            const isVisible = i < CONFIG.VISIBLE_SLOTS && item;

            return (
              <div
                key={`slot-${i}`}
                className="absolute flex items-center justify-center"
                style={{
                  width: CONFIG.HOLE_SIZE,
                  height: CONFIG.HOLE_SIZE,
                  top: `calc(50% - ${CONFIG.HOLE_SIZE / 2}px)`,
                  left: `calc(50% - ${CONFIG.HOLE_SIZE / 2}px)`,
                  transform: `translate(${x}px, ${y}px) rotate(${-rotation}deg)`,
                }}
              >
                 {isVisible ? (
                    <motion.div 
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ delay: i * 0.05 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleItemClick(i);
                        }}
                        className={`w-full h-full rounded-full overflow-hidden relative group bg-zinc-800 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8),0_0_15px_rgba(0,0,0,0.6)] cursor-pointer transition-all hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.8),0_0_25px_rgba(255,140,0,0.4)] border-2 ${i === 0 ? 'border-orange-500 ring-2 ring-orange-400/50' : 'border-gray-600/50 hover:border-orange-400/50'}`}
                    >
                        <img 
                            src={item.image} 
                            alt={item.label} 
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-125" 
                        />
                        {item.type === 'dish' && item.price && (
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent text-white text-[9px] text-center font-bold py-2 px-1 shadow-lg">
                                {item.price}
                            </div>
                        )}
                        {i === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-gradient-to-t from-orange-500/10 to-transparent rounded-full" />
                        )}
                    </motion.div>
                 ) : null}
              </div>
            );
          })}
          </AnimatePresenceSafe>
        </motion.div>

        {/* CENTER BUTTON AREA */}
        <motion.div 
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSelectActive}
          className="absolute z-50 rounded-full flex items-center justify-center cursor-pointer transition-transform"
          style={{ 
            width: CONFIG.BTN_SIZE,
            height: CONFIG.BTN_SIZE,
            top: '50%',
            left: '50%',
            marginTop: -(CONFIG.BTN_SIZE / 2),
            marginLeft: -(CONFIG.BTN_SIZE / 2),
          }}
        >
             <div 
                className="absolute inset-0 bg-contain bg-center bg-no-repeat drop-shadow-2xl"
                style={{ backgroundImage: `url(${ASSETS.BTN})` }} 
             />

             <AnimatePresenceSafe mode="wait">
                <motion.div
                    key={centerLabel}
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: 10 }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 300 }}
                    className="relative z-10 w-[75%] h-[75%] rounded-full overflow-hidden border-3 border-white/30 shadow-[inset_0_0_10px_rgba(0,0,0,0.5),0_0_20px_rgba(255,165,0,0.3)] bg-black/70 flex items-center justify-center"
                >
                    {navStack.length > 0 ? (
                         <img src={centerImage} className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity" alt="Center" />
                    ) : (
                        <div className="flex items-center justify-center bg-gradient-to-br from-orange-600/30 to-orange-900/30 w-full h-full">
                             <span className="text-4xl font-serif font-bold text-orange-400 drop-shadow-2xl">R</span>
                        </div>
                    )}
                </motion.div>
             </AnimatePresenceSafe>

             <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                 <div className="bg-black/60 backdrop-blur-xl px-4 py-1.5 rounded-full border border-orange-500/50 shadow-lg">
                    <span className="text-[11px] font-bold text-orange-300 uppercase tracking-widest drop-shadow-lg font-semibold">
                        {navStack.length > 0 ? "OPEN" : "SELECT"}
                    </span>
                 </div>
             </div>
        </motion.div>

      </div>

      {/* NAMEPLATE DISPLAY */}
      <div className="relative z-20 flex flex-col items-center mt-[-30px] mb-4">
         <div className="relative w-80 h-32 flex items-center justify-center">
            <img 
              src={ASSETS.NAMEPLATE} 
              alt="Nameplate" 
              className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl opacity-95"
            />
            <div className="relative z-10 text-center pt-6 px-10 w-full flex flex-col items-center">
                <AnimatePresenceSafe mode="wait">
                    <motion.div
                        key={activeItem?.id || "empty"}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.3 }}
                        className="flex flex-col items-center w-full"
                    >
                        <h2 className="text-xl font-bold text-gray-900 leading-tight line-clamp-1 font-serif max-w-[200px] drop-shadow-md" 
                            style={{ textShadow: '0px 2px 4px rgba(255,255,255,0.5)' }}>
                            {activeItem?.label || "RUCHIRA"}
                        </h2>
                        <div className="h-[2px] w-14 bg-gradient-to-r from-orange-900/50 via-orange-700 to-orange-900/50 my-1.5 shadow-md" />
                        <p className="text-orange-900 text-[10px] font-bold uppercase tracking-[0.3em] drop-shadow">
                            {activeItem?.type === 'kitchen' ? '🏠 Kitchen' : activeItem?.type === 'dish' ? '🍽️ Dish' : activeItem?.type === 'category' ? '📋 Category' : 'EXPLORE'}
                        </p>
                    </motion.div>
                </AnimatePresenceSafe>
            </div>
         </div>
      </div>
      
      {/* INSTRUCTIONS */}
      <div className="absolute bottom-6 text-orange-300/60 text-[10px] uppercase tracking-widest font-semibold drop-shadow-lg">
         🎵 Drag dial • Click center to select
      </div>

    </div>
  );
};

export default RotaryDial;
